// Phase P-LINK-MARRIAGE-TRIPLE
// Forward edges from /marriage/$nationality into the triple-key translation
// matrix /translate/english/$doctype/in/$location.
//
// Why: Foreign-spouse marriage registration in Thailand requires certified
// English-Thai translation of multiple documents (single status affidavit,
// passport, birth certificate, divorce decree, etc.). Deep-linking funnels
// PageRank from ~40 nationality pages into long-tail money pages.

import { Link } from "@tanstack/react-router";
import { ArrowRight, Languages } from "lucide-react";
import { getTranslationDocType } from "@/data/translation-doctypes";

const MARRIAGE_DOCTYPES = [
  "affidavit",
  "passport",
  "birth-certificate",
  "divorce-certificate",
  "marriage-certificate",
  "id-card",
  "house-registration",
  "medical-certificate",
] as const;

const DEFAULT_HUBS = [
  "bangkok-hq",
  "hub-sathorn-cbd",
  "hub-asoke-corridor",
  "hub-phuket-patong",
  "hub-chiangmai-nimman",
  "hub-pattaya",
] as const;

const HUB_LABEL_TH: Record<string, string> = {
  "bangkok-hq": "สำนักงานใหญ่ กรุงเทพฯ",
  "hub-asoke-corridor": "อโศก / สุขุมวิท",
  "hub-sathorn-cbd": "สาทร CBD",
  "hub-phuket-patong": "ภูเก็ต ป่าตอง",
  "hub-chiangmai-nimman": "เชียงใหม่ นิมมาน",
  "hub-pattaya": "พัทยา",
};

interface Props {
  nationalityTh: string;
  countryTh: string;
}

export default function MarriageTripleKeyLinks({ nationalityTh, countryTh }: Props) {
  type Card = { slug: string; d: NonNullable<ReturnType<typeof getTranslationDocType>>; hub: string };
  const cards: Card[] = MARRIAGE_DOCTYPES
    .map((slug, i): Card | null => {
      const d = getTranslationDocType(slug);
      if (!d) return null;
      return { slug: slug as string, d, hub: DEFAULT_HUBS[i % DEFAULT_HUBS.length] as string };
    })
    .filter((x): x is Card => x !== null)
    .slice(0, 6);

  if (cards.length === 0) return null;

  return (
    <section className="mt-14 border-t border-slate-200 pt-10">
      <div className="text-[11px] font-bold uppercase tracking-widest text-rose-700">
        แปลเอกสารแต่งงานกับชาว{nationalityTh}
      </div>
      <h2 className="mt-1 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
        เอกสารที่นิยมแปลภาษาอังกฤษ ก่อนจดทะเบียนสมรสกับชาว{countryTh}
      </h2>
      <p className="mt-2 max-w-3xl text-sm text-slate-600">
        เลือกเอกสารและฮับที่ใกล้คุณ — รับรอง MoJ + ตรา MFA พร้อมยื่นอำเภอ + สถานทูต{countryTh}
      </p>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map(({ slug, d, hub }) => (
          <Link
            key={`${slug}-${hub}`}
            to="/translate/$language/$doctype/in/$location"
            params={{ language: "english", doctype: slug, location: hub }}
            className="group flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
            data-track-zone="marriage-triple-deep"
          >
            <Languages className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
            <div className="flex-1">
              <div className="text-[10px] font-bold uppercase tracking-widest text-rose-700">
                {d.nameTh}
              </div>
              <div className="mt-0.5 font-display text-sm font-extrabold text-navy-deep group-hover:text-rose-600">
                แปล {d.nameTh} เป็นภาษาอังกฤษ
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                รับ/นัดที่ {HUB_LABEL_TH[hub] ?? hub}
              </div>
              <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700 group-hover:gap-1.5">
                ดูราคา + ขั้นตอน <ArrowRight className="h-3 w-3" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
